import hubspot from '@/assets/images/hubspot.png';
import * as Sentry from '@sentry/react';
import { HubSpotIntegration, Integration, WithId } from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { ColumnDef } from '@tanstack/react-table';
import { diff } from 'deep-diff';
import { Timestamp } from 'firebase/firestore';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  EditPencil,
  Globe,
  Lock,
  MoreVert,
  MultiplePagesEmpty,
  Plus,
  Trash,
} from 'iconoir-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CustomDataGrid } from '../components/CustomDataGrid/CustomDataGrid';
import { useAuditLog } from '../contexts/AuditLogContext';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { EditHubSpotIntegrationModal } from '../modals/EditHubSpotIntegrationModal';
import { EditWebhookIntegrationModal } from '../modals/EditWebhookIntegrationModal';
import {
  createHubSpotIntegration,
  duplicateWebhookIntegration,
  getIntegrations,
  updateIntegration,
} from '../services/IntegrationsService';
import { capitalize } from '../utils';
import IntegrationHealthBadge from './IntegrationHealthBadge';

type IntegrationItem = WithId<Integration>;

interface Props {
  dynamicPageId?: string;
}

export default function IntegrationsTable({ dynamicPageId }: Props) {
  const { show, hide } = useSnackbarContext();
  const { selectedAccount, role } = useContext(UserContext);
  const { dynamicPages } = useDashboardContext();
  const auditLog = useAuditLog();
  const [searchParams, setSearchParams] = useSearchParams();

  const [integrations, setIntegrations] = useState<IntegrationItem[]>();
  const [addIntegrationModalOpen, setAddIntegrationModalOpen] = useState(false);
  const [hubspotModalOpen, setHubspotModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<
    IntegrationItem | undefined
  >(undefined);
  const [preselectedConnectedAccountId, setPreselectedConnectedAccountId] =
    useState<string | undefined>(undefined);

  const canEditIntegrations = useMemo(() => {
    return role === 'admin';
  }, [role]);

  const fetchIntegrations = useCallback(() => {
    if (!selectedAccount) return;

    getIntegrations(selectedAccount.id, dynamicPageId)
      .then((integrations) => {
        setIntegrations(integrations);
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }, [selectedAccount, dynamicPageId]);

  // Handle HubSpot OAuth callback
  useEffect(() => {
    const hubspotSuccess = searchParams.get('hubspotSuccess');
    const hubspotError = searchParams.get('hubspotError');
    const connectedAccountId = searchParams.get('connectedAccountId');

    if (hubspotSuccess === 'true') {
      show('HubSpot connected successfully!', 'success');
      fetchIntegrations();
      // Open the modal to create a new integration with the connected account
      if (connectedAccountId) {
        setSelectedIntegration(undefined);
        setPreselectedConnectedAccountId(connectedAccountId);
        setHubspotModalOpen(true);
      }
      // Clear the URL params
      searchParams.delete('hubspotSuccess');
      searchParams.delete('connectedAccountId');
      setSearchParams(searchParams);
    } else if (hubspotError) {
      show(`HubSpot connection failed: ${hubspotError}`, 'warn');
      searchParams.delete('hubspotError');
      setSearchParams(searchParams);
    }
  }, [
    searchParams,
    setSearchParams,
    show,
    fetchIntegrations,
    selectedAccount,
    dynamicPageId,
  ]);

  useEffect(() => {
    setIntegrations(undefined);

    if (!selectedAccount) return;

    fetchIntegrations();
  }, [selectedAccount, dynamicPageId, fetchIntegrations]);

  const onDuplicateIntegration = useCallback(
    (integrationId: string) => {
      const snack = show('Duplicating integration...');

      duplicateWebhookIntegration(selectedAccount!.id, integrationId)
        .then(() => {
          show('Integration duplicated successfully');
          fetchIntegrations();

          auditLog.log(
            'create',
            'Duplicate integration',
            'integration',
            integrationId,
          );
        })
        .catch((error) => {
          show('Error duplicating integration');
          Sentry.captureException(error);
        })
        .finally(() => {
          hide(snack.id);
        });
    },
    [auditLog, fetchIntegrations, hide, selectedAccount, show],
  );

  const onDuplicateHubSpotIntegration = useCallback(
    (integration: IntegrationItem & { provider: 'hubspot' }) => {
      const snack = show('Duplicating HubSpot integration...');

      const hubspotIntegration = integration as HubSpotIntegration & {
        id: string;
      };

      createHubSpotIntegration(
        selectedAccount!.id,
        hubspotIntegration.connectedAccountId,
        {
          name: `${hubspotIntegration.name} (Copy)`,
          type: hubspotIntegration.type,
          status: 'active',
          dynamicPageIds: hubspotIntegration.dynamicPageIds,
          formIds: hubspotIntegration.formIds,
          fieldMappings: hubspotIntegration.fieldMappings,
          eventTypes:
            'eventTypes' in hubspotIntegration
              ? hubspotIntegration.eventTypes
              : undefined,
        },
      )
        .then(() => {
          show('HubSpot integration duplicated successfully');
          fetchIntegrations();

          auditLog.log(
            'create',
            'Duplicate HubSpot integration',
            'integration',
            integration.id,
          );
        })
        .catch((error) => {
          show('Error duplicating HubSpot integration');
          Sentry.captureException(error);
        })
        .finally(() => {
          hide(snack.id);
        });
    },
    [auditLog, fetchIntegrations, hide, selectedAccount, show],
  );

  const onToggleIntegrationStatus = useCallback(
    (integration: IntegrationItem, newStatus: Integration['status']) => {
      updateIntegration(selectedAccount!.id, integration.id, {
        status: newStatus,
      })
        .then(() => {
          show('Integration is now ' + capitalize(newStatus));
          fetchIntegrations();
          auditLog.log(
            'update',
            'Update integration status',
            'integration',
            integration.id,
            undefined,
            diff({ status: integration.status }, { status: newStatus }),
          );
        })
        .catch((error) => {
          show('Error updating integration status');
          Sentry.captureException(error);
        });
    },
    [auditLog, fetchIntegrations, selectedAccount, show],
  );

  const onDeleteIntegration = useCallback(
    (integration: IntegrationItem) => {
      updateIntegration(selectedAccount!.id, integration.id, {
        status: 'deleted',
      })
        .then(() => {
          show('Integration deleted successfully');
          fetchIntegrations();
          auditLog.log(
            'delete',
            'Delete integration',
            'integration',
            integration.id,
          );
        })
        .catch((error) => {
          show('Error deleting integration');
          Sentry.captureException(error);
        });
    },
    [auditLog, fetchIntegrations, selectedAccount, show],
  );

  const columns: ColumnDef<IntegrationItem>[] = useMemo(() => {
    return [
      {
        header: 'Name',
        id: 'name',
        accessorKey: 'name',
        size: 300,
      },
      {
        header: 'Status',
        id: 'status',
        accessorKey: 'status',
        size: 120,
        cell: (info) => (
          <Badge variant={info.getValue() === 'active' ? 'success' : 'error'}>
            {capitalize(info.row.original.status)}
          </Badge>
        ),
      },
      {
        header: 'Health',
        id: 'health',
        size: 120,
        cell: ({ row }) => (
          <IntegrationHealthBadge
            lastErrors={row.original.lastErrors}
            consecutiveFailures={row.original.consecutiveFailures}
          />
        ),
      },
      {
        header: 'Type',
        id: 'type',
        accessorKey: 'type',
        size: 250,
        cell: (info) =>
          info.getValue() === 'new_lead'
            ? 'On New Lead'
            : info.getValue() === 'new_partial_lead'
              ? 'On Partial Lead'
              : `On Event: ${(info.row.original as any).eventTypes?.join(', ')}`,
      },
      {
        header: 'Authentication',
        id: 'authentication',
        accessorFn: (row) =>
          ('authentication' in row ? row.authentication?.type : undefined) ===
          'apiKey'
            ? 'API Key'
            : 'Public/Private Key',
        size: 180,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <Lock className="size-4 text-gray-500" />
            {info.getValue() as string}
          </div>
        ),
      },
      {
        header: 'Created At',
        id: 'createdAt',
        accessorFn: (row) =>
          row.createdAt
            ? new Timestamp(
                row.createdAt._seconds,
                row.createdAt._nanoseconds,
              ).toDate()
            : undefined,
        size: 180,
        cell: (info) => info.getValue()?.toLocaleString(),
      },
      ...(dynamicPageId
        ? []
        : ([
            {
              header: 'Dynamic Pages',
              id: 'dynamicPageIds',
              accessorFn: (row) =>
                'dynamicPageIds' in row && row.dynamicPageIds?.length
                  ? row.dynamicPageIds
                      .map((id) => dynamicPages?.find((p) => p.id === id))
                      .filter((x) => !!x)
                  : undefined,
              size: 250,
              cell: (info) => {
                const items = info.getValue() as any[] | undefined;
                if (!items?.length) return null;

                const maxVisible = 3;
                const hasMore = items.length > maxVisible;
                const visibleItems = items.slice(0, maxVisible);
                const hiddenItems = items.slice(maxVisible);

                return (
                  <div className="flex flex-wrap gap-1">
                    {visibleItems.map((x: any) => (
                      <Badge key={x.id} variant={'success'}>
                        {x.data().name}
                      </Badge>
                    ))}
                    {hasMore && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="bg-base-100 text-base-600 inline-flex cursor-default items-center rounded-full px-2 py-0.5 text-xs font-medium">
                            +{hiddenItems.length} more
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="flex flex-col gap-1">
                            {hiddenItems.map((x: any) => (
                              <span key={x.id}>{x.data().name}</span>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                );
              },
            },
          ] satisfies ColumnDef<IntegrationItem>[])),
      ...(canEditIntegrations
        ? ([
            {
              header: '',
              id: 'actions',
              size: 100,
              cell: ({ row }) => {
                // const integration = info.row.original;
                return (
                  <div className="flex items-center gap-2">
                    <Button
                      size={'icon'}
                      type={'button'}
                      variant={'outline'}
                      className={'rounded-2lg'}
                      onClick={() => {
                        setSelectedIntegration(row.original);
                        if (row.original.provider === 'hubspot') {
                          setHubspotModalOpen(true);
                        } else {
                          setAddIntegrationModalOpen(true);
                        }
                      }}
                    >
                      <EditPencil className="h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size={'icon'}
                          type={'button'}
                          variant={'outline'}
                          className={'rounded-2lg'}
                        >
                          <MoreVert
                            strokeWidth={3}
                            className="h-4 w-4"
                            width={20}
                            height={20}
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side={'bottom'} align={'end'}>
                        <DropdownMenuItem
                          onClick={() =>
                            row.original.provider === 'hubspot'
                              ? onDuplicateHubSpotIntegration(
                                  row.original as IntegrationItem & {
                                    provider: 'hubspot';
                                  },
                                )
                              : onDuplicateIntegration(row.original.id)
                          }
                        >
                          <MultiplePagesEmpty className="h-4 w-4" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            onToggleIntegrationStatus(
                              row.original,
                              row.original.status === 'active'
                                ? 'disabled'
                                : 'active',
                            )
                          }
                        >
                          {row.original.status !== 'active' ? (
                            <>
                              <ArrowUpCircle className="h-4 w-4" />
                              Enable
                            </>
                          ) : (
                            <>
                              <ArrowDownCircle className="h-4 w-4" />
                              Disable
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          variant={'destructive'}
                          onClick={() => onDeleteIntegration(row.original)}
                        >
                          <Trash className="h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              },
            },
          ] satisfies ColumnDef<IntegrationItem>[])
        : []),
    ] satisfies ColumnDef<IntegrationItem>[];
  }, [
    canEditIntegrations,
    dynamicPageId,
    dynamicPages,
    onDeleteIntegration,
    onDuplicateHubSpotIntegration,
    onDuplicateIntegration,
    onToggleIntegrationStatus,
  ]);

  return (
    <>
      <div className="flex h-full w-full flex-col overflow-hidden">
        <EditWebhookIntegrationModal
          open={addIntegrationModalOpen}
          onClose={() => {
            setAddIntegrationModalOpen(false);
            setSelectedIntegration(undefined);
          }}
          onSaved={() => {
            fetchIntegrations();
          }}
          integrationId={selectedIntegration?.id}
          integration={
            selectedIntegration?.provider === 'webhook'
              ? selectedIntegration
              : undefined
          }
          forceDynamicPagesConnection={
            dynamicPageId ? [dynamicPageId] : undefined
          }
        />
        <EditHubSpotIntegrationModal
          open={hubspotModalOpen}
          onClose={() => {
            setHubspotModalOpen(false);
            setSelectedIntegration(undefined);
            setPreselectedConnectedAccountId(undefined);
          }}
          onSaved={() => {
            fetchIntegrations();
          }}
          integrationId={selectedIntegration?.id}
          integration={
            selectedIntegration?.provider === 'hubspot'
              ? (selectedIntegration as HubSpotIntegration)
              : undefined
          }
          forceDynamicPagesConnection={
            dynamicPageId ? [dynamicPageId] : undefined
          }
          preselectedConnectedAccountId={preselectedConnectedAccountId}
        />
        <div className="mb-5 flex w-full flex-wrap items-start justify-between gap-4 overflow-hidden">
          <div className="sm:flex-auto">
            <h1 className="text-base leading-6 font-semibold text-gray-900">
              Integrations
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage your integrations with other services.
            </p>
          </div>
          {canEditIntegrations && (
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <Plus className="h-4.5 w-4.5 text-white" />
                    Add
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side={'bottom'} align={'end'}>
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedIntegration(undefined);
                      setAddIntegrationModalOpen(true);
                    }}
                  >
                    <Globe className="h-4 w-4" />
                    Webhook
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedIntegration(undefined);
                      setHubspotModalOpen(true);
                    }}
                  >
                    <img src={hubspot} alt="HubSpot" className="h-4 w-4" />
                    HubSpot
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
        <div className="relative flex-1 overflow-x-auto">
          <CustomDataGrid
            logicalName="integrations"
            columns={columns}
            data={integrations}
            rowClassName={'min-h-16'}
            virtualized={false}
            initialState={{
              columnPinning: { right: ['actions'] },
            }}
          />
        </div>
      </div>
    </>
  );
}
